import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import { projectApi } from '../components/userProject/api'

/**
 * Support des plateformes (PrimeStation…) en direct : socket <plateforme>/support-admin, ouvert avec un ticket de 60 s
 * demandé au serveur du tracker (/projects/<plateforme>/support/ticket : il ajoute le token interne) ; nouveau ticket à
 * chaque reconnexion. Donne les messages non lus (pastille du menu) et les événements à la page « Support plateformes ».
 * Plateforme pas branchée sur ce tracker (ticket refusé) : rien, pas de pastille.
 */
export const SUPPORT_PROJECT = 'primestation'
const SupportContext = createContext({ unread: 0, connected: false, on: () => () => { }, refresh: () => { } })

export const SupportProvider = ({ children }) => {
    const [unread, setUnread] = useState(0)
    const [connected, setConnected] = useState(false)
    const listeners = useRef(new Set())
    const api = useRef(projectApi(SUPPORT_PROJECT))

    const refresh = useCallback(async () => {
        try { setUnread((await api.current.get('/support/unread')).unread || 0) } catch { }
    }, [])

    useEffect(() => {
        let socket = null
        let stop = false
        const start = async () => {
            let first
            try { first = await api.current.post('/support/ticket') } catch { return }
            if (stop || !first?.url) return
            socket = io(`${first.url}/support-admin`, {
                transports: ['websocket'],
                auth: (cb) => api.current.post('/support/ticket').then((t) => cb({ ticket: t.ticket }), () => cb({})),
            })
            socket.on('connect', () => { setConnected(true); refresh() })
            socket.on('disconnect', () => setConnected(false))
            // message (d'un user ou d'un admin), lu : pastille relue, page prévenue
            const fire = (type) => (data) => {
                refresh()
                listeners.current.forEach((fn) => fn(type, data))
            }
            socket.on('support:message', fire('message'))
            socket.on('support:read', fire('read'))
        }
        start()
        // retour sur l'onglet du tracker : pastille relue (au cas où le direct aurait raté quelque chose)
        const onFocus = () => document.visibilityState === 'visible' && refresh()
        refresh()
        document.addEventListener('visibilitychange', onFocus)
        window.addEventListener('focus', onFocus)
        return () => {
            stop = true
            socket?.close()
            document.removeEventListener('visibilitychange', onFocus)
            window.removeEventListener('focus', onFocus)
        }
    }, [refresh])

    /** La page écoute : fn(type 'message' | 'read', data) → fonction pour arrêter. */
    const on = useCallback((fn) => {
        listeners.current.add(fn)
        return () => listeners.current.delete(fn)
    }, [])

    return <SupportContext.Provider value={{ unread, connected, on, refresh }}>{children}</SupportContext.Provider>
}

export const useSupport = () => useContext(SupportContext)
